import { JSX } from 'react';
import mlCopySectionVariants from './ml-copy-section.variants';
import classNames from 'classnames';

// Components
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { MlCard } from '@/components/molecules/ml-card';

// Types
import { IMlCopySectionProps } from './ml-copy-section.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

/**
 * `MlCopySection` is a responsive layout component that combines a main copy block
 * (e.g. `MlCopyBlock`) with optional side cards (e.g. `MlCard`) displayed either above
 * or beside the content depending on screen size.
 *
 * On mobile/tablet:
 * - Cards stack above the copy content in a single column layout.
 *
 * On desktop (`lg+`):
 * - Cards shift to the right side of the layout, while the copy block is centered.
 *
 */

export const MlCopySection = ({
  children,
  cardProps = [],
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
}: IMlCopySectionProps): JSX.Element => {
  const classes = mlCopySectionVariants({ topSpacing, bottomSpacing });
  return (
    <section className="container">
      {topDivider && (
        <AtDivider
          decorative
          color={EDividerColors.GREY}
          direction={EDividerDirectionalVariants.HORIZONTAL}
          intent={EDividerVariants.THIN}
        />
      )}
      <div
        className={classNames(
          classes,
          'grid grid-cols-1 gap-y-12 md:grid-cols-6 lg:grid-cols-12',
        )}
      >
        <aside className="col-span-1 flex flex-col gap-3 md:col-span-6 md:flex-row lg:hidden">
          {cardProps?.map((card, index) => {
            return (
              <div className="w-full md:w-1/2 lg:w-auto" key={index}>
                <MlCard {...card} className="p-11" />
              </div>
            );
          })}
        </aside>
        <div className="col-span-1 max-w-full md:col-span-6 lg:col-span-5 lg:col-start-3">
          {children}
        </div>
        <aside className="hidden lg:col-span-3 lg:col-start-10 lg:flex lg:flex-col lg:gap-12">
          {cardProps?.map((card, index) => {
            return (
              <div className="" key={index}>
                <MlCard {...card} className="p-11" />
              </div>
            );
          })}
        </aside>
      </div>
      {bottomDivider && (
        <AtDivider
          decorative
          color={EDividerColors.GREY}
          direction={EDividerDirectionalVariants.HORIZONTAL}
          intent={EDividerVariants.THIN}
        />
      )}
    </section>
  );
};
